import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Pagination,
  Alert,
  useTheme,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
  GroupRemove as GroupRemoveIcon,
  SwapHoriz as SwapHorizIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { DataTable, FilterBar, SchoolPicker, EmptyState } from './primitives';
import { ROLES, isTeacher, isSchoolScopedRole } from '../../constants/roles';
import { useQueryState, useScrollRestore } from '../../hooks/useQueryState';
import StudentDrawer from './StudentDrawer';
import { deriveStudentStatus, STATUS_META, formatLastActive } from './studentStatus';

const PAGE_SIZE = 25;

// Every list-view filter this page understands, doubling as
// useQueryState()'s schema — see that hook's own docstring for how a key's
// default value here decides its URL coercion, and why a value equal to
// its default never appears in the URL at all.
const QUERY_DEFAULTS = {
  q: '',
  school: '',
  classId: '',
  status: 'all',
  grade: '',
  sort: 'name',
  order: 'asc',
  page: 1,
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'no_access', label: 'No access' },
];

// Sorts "K" first, then numeric grades ascending, then anything else
// (a school's own free-text label — see User.grade's docstring in
// app/models.py, which is deliberately unvalidated) alphabetically after.
function compareGrades(a, b) {
  if (a === b) return 0;
  if (a === 'K') return -1;
  if (b === 'K') return 1;
  const an = Number(a);
  const bn = Number(b);
  const aIsNum = a !== '' && !Number.isNaN(an);
  const bIsNum = b !== '' && !Number.isNaN(bn);
  if (aIsNum && bIsNum) return an - bn;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.localeCompare(b);
}

// Compact "N classes · Algebra 1, Biology 9" cell — never one chip per
// class (the old UsersView table did that and fell over past ~3 classes).
// The full list is still one hover away via the native title tooltip.
function ClassesSummaryCell({ classes }) {
  if (!classes || classes.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
        No classes
      </Typography>
    );
  }
  const names = classes.map((c) => c.name).join(', ');
  return (
    <Tooltip title={names}>
      <Box sx={{ maxWidth: 220 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
          {classes.length} class{classes.length === 1 ? '' : 'es'}
        </Typography>
        <Typography variant="caption" noWrap sx={{ display: 'block', color: adminColors.inkSoft }}>
          {names}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// Students list — replaces UsersView.js on the /admin/students route.
// Originally titled "All Users" and showed every role (with a Role filter/
// column); split into this STUDENT-only page plus the sibling StaffPage.js
// (teachers/admins, at /admin/staff) as part of the admin-console v2 staff
// split — see that file's own header comment for the exact scoping matrix.
// UsersView stays in the tree (still reachable if anything still imports it
// directly) until a later cleanup removes it; this is a parallel
// implementation, not an edit of that file.
//
// `groups` is the same GET /api/groups array AdminDashboardV2 already
// fetches for ClassesView/ClassUserAssignmentView (role-scoped server-side:
// a TEACHER only ever sees their own classes here, a SCHOOL_ADMIN only
// their own school's) — reused for the class filter and every "which class"
// picker below instead of a second fetch of the same data.
export default function StudentsPage({
  groups,
  onRoleChange,
  onSchoolChange,
  onDeleteUser,
  onAccessToggle,
  onResetPassword,
  onOpenAddUserDialog,
  currentUserRole,
  currentUser,
  notify,
  confirmAction,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams();
  const theme = useTheme();
  const [qs, setQueryState] = useQueryState(QUERY_DEFAULTS);

  const isTeacherRole = isTeacher(currentUserRole);
  // SCHOOL_ADMIN and TEACHER are both locked to one school server-side —
  // same isSchoolScopedRole check UsersView already used to disable its
  // per-row School Select, reused here to decide whether a School *filter*
  // or a "Move school" bulk action are even meaningful controls to show.
  const isSchoolLocked = isSchoolScopedRole(currentUserRole);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Bulk-action dialogs
  const [bulkDialog, setBulkDialog] = useState(null); // 'add' | 'remove' | 'moveSchool' | null
  const [addClassTarget, setAddClassTarget] = useState('');
  const [addClassBusy, setAddClassBusy] = useState(false);
  const [removeClassTarget, setRemoveClassTarget] = useState('');
  const [removeClassBusy, setRemoveClassBusy] = useState(false);
  const [moveSchoolTarget, setMoveSchoolTarget] = useState('');
  const [moveSchoolBusy, setMoveSchoolBusy] = useState(false);
  const [moveSchoolProgress, setMoveSchoolProgress] = useState(null); // {done, total}
  const [moveSchoolResult, setMoveSchoolResult] = useState(null); // {succeeded:[], failed:[{id, username, message}]}

  // Every distinct `grade` value seen across every fetch so far this
  // session — there's no "distinct grades" endpoint (grade is free text,
  // unvalidated per school; see User.grade's docstring in app/models.py),
  // so the Grade filter's own option list is built up from real values as
  // pages load rather than a guessed, possibly-wrong fixed taxonomy. Starts
  // empty and only grows; a grade that hasn't been seen on any fetched page
  // yet just isn't offered as a filter option until it has.
  const [seenGrades, setSeenGrades] = useState(() => new Set());

  const statusActive = qs.status !== QUERY_DEFAULTS.status;
  const gradeActive = qs.grade !== QUERY_DEFAULTS.grade;

  const fetchList = useCallback(
    async (signal) => {
      setLoadingList(true);
      setListError(null);
      const params = new URLSearchParams();
      if (qs.q) params.set('search', qs.q);
      // Every row on this page is a student now (see the phase-2 report) —
      // sent unconditionally, including for a TEACHER, whose `scope=
      // my_classes` restriction below already implies STUDENT-only rows;
      // explicit here rather than relying on that side effect.
      params.set('role', ROLES.STUDENT);
      if (!isTeacherRole && qs.school) params.set('school', qs.school);
      if (qs.classId) params.set('group_id', qs.classId);
      params.set('include', 'groups');
      if (isTeacherRole) params.set('scope', 'my_classes');
      params.set('sort', qs.sort);
      params.set('order', qs.order);
      // Grade shares the exact same "no server filter, fetch 100 and filter
      // client-side" workaround status already uses above (there's no
      // `grade` query param either) — both can be active together, and
      // either alone still triggers the wider fetch.
      const needsClientFilter = statusActive || gradeActive;
      if (needsClientFilter) {
        params.set('page', '1');
        params.set('page_size', '100');
      } else {
        params.set('page', String(qs.page));
        params.set('page_size', String(PAGE_SIZE));
      }
      try {
        const res = await authFetch(
          `${config.API_BASE_URL}/api/admin/users?${params.toString()}`,
          {},
          { signal }
        );
        const data = await handleApiResponse(res, navigate);
        if (data === null || signal?.aborted) return; // 401 — already redirected

        // Grow the seen-grades set from whatever this fetch actually
        // returned, regardless of whether the grade filter itself is
        // active — every page viewed makes the filter's own option list a
        // little more complete.
        const gradesThisPage = (data.users || []).map((u) => u.grade).filter((g) => g && g.trim());
        if (gradesThisPage.length) {
          setSeenGrades((prev) => {
            const next = new Set(prev);
            let changed = false;
            gradesThisPage.forEach((g) => {
              if (!next.has(g)) { next.add(g); changed = true; }
            });
            return changed ? next : prev;
          });
        }

        // Narrowing a filter (or a deep link landing past the last page)
        // can leave `page` pointing past the *real* last page — clamped
        // here, inline in the fetch success handler using this response's
        // own total, same as SafetyFlagsView's fetchFlags does. Doing this
        // in a separate effect keyed on `total` state instead (the previous
        // approach) breaks on first mount: that effect runs before this
        // fetch's response ever lands, sees the still-initial `total = 0`,
        // computes totalPages = 1, and clamps a real deep link like
        // `?page=3` straight back down to page 1 before the actual data
        // (which may well have had 3+ pages) has even arrived.
        let newTotal;
        if (needsClientFilter) {
          const filtered = (data.users || []).filter((u) => {
            if (statusActive && deriveStudentStatus(u) !== qs.status) return false;
            if (gradeActive && (u.grade || '') !== qs.grade) return false;
            return true;
          });
          newTotal = filtered.length;
          const start = (qs.page - 1) * PAGE_SIZE;
          setRows(filtered.slice(start, start + PAGE_SIZE));
          setTotal(newTotal);
        } else {
          newTotal = data.total || 0;
          setRows(data.users || []);
          setTotal(newTotal);
        }
        const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
        if (qs.page > newTotalPages) {
          setQueryState({ page: newTotalPages });
        }
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted) return;
        setListError(`Couldn't load students. ${describeApiError(err)}`);
      } finally {
        if (!signal?.aborted) setLoadingList(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statusActive/gradeActive are derived from qs.status/qs.grade, already listed
    [qs.q, qs.school, qs.classId, qs.status, qs.grade, qs.sort, qs.order, qs.page, isTeacherRole, navigate, setQueryState]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList]);

  // A stale selection pointing at rows that just scrolled off-page (new
  // search/filter/sort/page) is worse than an empty one — bulk actions
  // would silently act on students no longer even visible.
  useEffect(() => {
    setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.q, qs.school, qs.classId, qs.status, qs.grade, qs.sort, qs.order, qs.page]);

  // AdminDashboardV2's main content area scrolls at the window level (no
  // inner `overflow: auto` container — see App.js/AdminDashboardV2.js) —
  // useScrollRestore expects an element with its own scrollTop/scroll
  // listener, so this adapts `window` to that same shape rather than
  // wrapping the table in a second, app-wide-inconsistent scroll
  // container just for this one page.
  const windowScrollRefBox = useRef(null);
  if (!windowScrollRefBox.current) {
    windowScrollRefBox.current = {
      get scrollTop() {
        return window.scrollY || window.pageYOffset || 0;
      },
      set scrollTop(v) {
        window.scrollTo(0, v);
      },
      addEventListener: (type, handler, opts) => window.addEventListener(type, handler, opts),
      removeEventListener: (type, handler) => window.removeEventListener(type, handler),
    };
  }
  useScrollRestore(windowScrollRefBox, `students-list:${location.search}`);

  const rowsById = useMemo(() => Object.fromEntries(rows.map((r) => [r.id, r])), [rows]);

  const classOptions = useMemo(() => {
    const list = (groups || []).filter((g) => {
      if (!qs.school) return true;
      return (g.school || '').trim().toLowerCase() === qs.school.trim().toLowerCase();
    });
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups, qs.school]);

  const handleRowClick = (row) => {
    navigate({ pathname: `/admin/students/${encodeURIComponent(row.username)}`, search: location.search });
  };

  const closeDrawer = () => {
    navigate({ pathname: '/admin/students', search: location.search });
  };

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'name',
        // Every row here is a STUDENT now (see the phase-2 report) — the
        // column reads "Student" for every viewer, not just a teacher.
        label: 'Student',
        sortable: true,
        render: (row) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
              {row.username || 'Unknown'}
            </Typography>
            {/* GET /api/admin/users doesn't return `email` today (see
                report) — rendered defensively so this starts working the
                moment the backend adds it, with no client change. */}
            {row.email && (
              <Typography variant="caption" noWrap sx={{ display: 'block', color: adminColors.inkSoft, maxWidth: 220 }}>
                {row.email}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        // No server-side sort key for `grade` (see GET /api/admin/users —
        // it has no `sort=grade` case) — not marked `sortable` rather than
        // wiring a column header that silently no-ops when clicked.
        key: 'grade',
        label: 'Grade',
        render: (row) => (
          <Typography variant="body2" sx={{ color: row.grade ? adminColors.ink : adminColors.inkSoft }}>
            {row.grade || '—'}
          </Typography>
        ),
      },
      {
        key: 'school',
        label: 'School',
        sortable: true,
        render: (row) => (
          <Typography variant="body2" sx={{ color: row.school ? adminColors.ink : adminColors.inkSoft }}>
            {row.school || 'Unassigned'}
          </Typography>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => {
          const status = deriveStudentStatus(row);
          const meta = STATUS_META[status];
          return (
            <Box>
              <Chip
                label={meta.label}
                size="small"
                sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, height: 22, fontSize: '0.72rem' }}
              />
              <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, mt: 0.5 }}>
                {formatLastActive(row.last_active)}
              </Typography>
            </Box>
          );
        },
      },
      {
        key: 'classes',
        label: 'Classes',
        render: (row) => <ClassesSummaryCell classes={row.classes} />,
      },
    ];
    return cols;
  }, []);

  const bulkActions = useMemo(() => {
    const actions = [
      { label: 'Add to class', icon: <GroupAddIcon fontSize="small" />, onClick: () => setBulkDialog('add') },
      { label: 'Remove from class', icon: <GroupRemoveIcon fontSize="small" />, onClick: () => setBulkDialog('remove') },
    ];
    // "Move school" mirrors UsersView's row-level School Select, which was
    // disabled outright for isSchoolScopedRole (SCHOOL_ADMIN/TEACHER) — a
    // SCHOOL_ADMIN can technically PUT /users/{id}/school but only back
    // onto their own (already current) school, so the control has nothing
    // useful to do for them; MASTER_ADMIN/legacy admin only.
    if (!isSchoolLocked) {
      actions.push({ label: 'Move school', icon: <SwapHorizIcon fontSize="small" />, onClick: () => setBulkDialog('moveSchool') });
    }
    return actions;
  }, [isSchoolLocked]);

  const closeBulkDialog = () => {
    if (addClassBusy || removeClassBusy || moveSchoolBusy) return;
    setBulkDialog(null);
    setAddClassTarget('');
    setRemoveClassTarget('');
    setMoveSchoolTarget('');
    setMoveSchoolProgress(null);
    setMoveSchoolResult(null);
  };

  const handleBulkAdd = async () => {
    if (!addClassTarget) return;
    setAddClassBusy(true);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${addClassTarget}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Bare-array body — matches POST /groups/{id}/assign's
        // `user_ids: list[int] = Body(...)` (no `{user_ids: [...]}` wrapper).
        body: JSON.stringify(selectedIds),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      const groupName = groups.find((g) => g.id === addClassTarget)?.name || 'the class';
      notify(`${selectedIds.length} student${selectedIds.length === 1 ? '' : 's'} added to ${groupName}.`, 'success');
      closeBulkDialog();
      setSelectedIds([]);
      fetchList();
    } catch (err) {
      notify(`Couldn't add those students. ${describeAdminError(err)}`, 'error');
    } finally {
      setAddClassBusy(false);
    }
  };

  // Only classes at least one *currently selected* row is actually in —
  // narrower and more useful than every class in the school, and works
  // straight off the page data (`include=groups` already attached each
  // row's `classes`), no extra fetch.
  const removableClassOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!selectedIds.includes(r.id)) return;
      (r.classes || []).forEach((c) => {
        if (!map.has(c.id)) map.set(c.id, c);
      });
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [rows, selectedIds]);

  const handleBulkRemove = async () => {
    if (!removeClassTarget) return;
    setRemoveClassBusy(true);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${removeClassTarget}/members/bulk-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: selectedIds }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      const groupName = removableClassOptions.find((g) => g.id === removeClassTarget)?.name || 'the class';
      const notFoundCount = (data.not_found || []).length;
      notify(
        notFoundCount
          ? `${data.removed} removed from ${groupName}; ${notFoundCount} weren't in it.`
          : `${data.removed} removed from ${groupName}.`,
        'success'
      );
      closeBulkDialog();
      setSelectedIds([]);
      fetchList();
    } catch (err) {
      notify(`Couldn't remove those students. ${describeAdminError(err)}`, 'error');
    } finally {
      setRemoveClassBusy(false);
    }
  };

  const handleMoveSchool = async () => {
    if (!moveSchoolTarget) return;
    setMoveSchoolBusy(true);
    const succeeded = [];
    const failed = [];
    setMoveSchoolProgress({ done: 0, total: selectedIds.length });
    for (let i = 0; i < selectedIds.length; i += 1) {
      const userId = selectedIds[i];
      const rowUser = rowsById[userId];
      try {
        const res = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/school`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ school: moveSchoolTarget }),
        });
        const data = await handleApiResponse(res, navigate);
        if (data === null) {
          // 401 — already redirected away, no point continuing the loop.
          failed.push({ id: userId, username: rowUser?.username || `#${userId}`, message: 'Session expired.' });
          break;
        }
        succeeded.push(userId);
      } catch (err) {
        failed.push({ id: userId, username: rowUser?.username || `#${userId}`, message: describeAdminError(err) });
      }
      setMoveSchoolProgress({ done: i + 1, total: selectedIds.length });
    }
    setMoveSchoolBusy(false);
    setMoveSchoolResult({ succeeded, failed });
    if (succeeded.length) {
      notify(`${succeeded.length} student${succeeded.length === 1 ? '' : 's'} moved.`, failed.length ? 'warning' : 'success');
    } else if (failed.length) {
      notify("That move couldn't be completed.", 'error');
    }
  };

  const finishMoveSchool = () => {
    closeBulkDialog();
    setSelectedIds([]);
    fetchList();
  };

  // "Are any real filters narrowing the result set" — sort/order and page
  // aren't filters (a fresh Z–A sort on an empty school shouldn't read as
  // "no matches, try clearing filters").
  const hasActiveFilters = ['q', 'school', 'classId', 'status', 'grade'].some(
    (key) => (qs[key] ?? '') !== (QUERY_DEFAULTS[key] ?? '')
  );

  const emptyStateNode = hasActiveFilters ? (
    <EmptyState
      icon={<PeopleAltIcon />}
      title="No matches"
      description="Try a different search, or clear filters to see everyone."
      action={
        <Button variant="outlined" onClick={() => setQueryState({ ...QUERY_DEFAULTS })} sx={TOUCH_HIT_SX}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<PeopleAltIcon />}
      title="No students yet"
      description={
        isTeacherRole
          ? "Once you're assigned to a class, its students will show up here — or invite one directly."
          : 'Use "Add New User" to create or invite one.'
      }
      action={
        <Button variant="contained" onClick={onOpenAddUserDialog} sx={TOUCH_HIT_SX}>
          {isTeacherRole ? 'Invite Student' : 'Add New User'}
        </Button>
      }
    />
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (qs.page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(qs.page * PAGE_SIZE, total);

  const sortedGroupsForPicker = useMemo(
    () => [...(groups || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [groups]
  );

  // seenGrades ∪ the currently-active grade filter (if any) — the union
  // guard means a deep link like `?grade=9` renders its own filter chip as
  // a real, selected option immediately, even before any page fetch has
  // had a chance to observe a "9" on a loaded row.
  const gradeFilterOptions = useMemo(() => {
    const all = new Set(seenGrades);
    if (qs.grade) all.add(qs.grade);
    return Array.from(all).sort(compareGrades);
  }, [seenGrades, qs.grade]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: adminColors.ink }}>
            {isTeacherRole ? 'My Students' : 'Students'}
          </Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.5 }}>
            {isTeacherRole
              ? 'Students enrolled in your classes.'
              : 'Every student, across every school you can see.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onOpenAddUserDialog}
          sx={{
            backgroundColor: adminColors.accent,
            '&:hover': { backgroundColor: adminColors.accentDark },
            borderRadius: radius.control,
            px: 3,
            py: 1,
            ...TOUCH_HIT_SX,
          }}
        >
          {isTeacherRole ? 'Invite Student' : 'Add New User'}
        </Button>
      </Box>

      {listError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchList()}>
              Retry
            </Button>
          }
        >
          {listError}
        </Alert>
      )}

      <FilterBar
        queryState={qs}
        setQueryState={setQueryState}
        defaults={QUERY_DEFAULTS}
        searchKey="q"
        searchPlaceholder="Search by name or school…"
        resetOnSearch={['page']}
        sx={{ mb: 2.5 }}
      >
        {!isSchoolLocked && (
          <SchoolPicker
            value={qs.school}
            onChange={(v) => setQueryState({ school: v, page: QUERY_DEFAULTS.page })}
            currentUserRole={currentUserRole}
            emptyLabel="All schools"
            label="School"
            size="small"
            fullWidth={false}
            sx={{ minWidth: 170 }}
          />
        )}

        <FormControl size="small" sx={{ minWidth: 170 }}>
          {/* `shrink` explicitly — same `displayEmpty` gotcha
              primitives/SchoolPicker.js's InputLabel comment explains: with
              displayEmpty on, the Select always renders visible content
              (a real class name, or "All classes") even at its default
              value ("" / unfiltered), but MUI's own shrink logic only looks
              at `value` truthiness, not at whether displayEmpty is making
              something visible anyway — so the label sat drawn directly on
              top of "All classes" at the default filter. Unlike
              SchoolPicker's `allowUnassigned`, this Select is always
              displayEmpty, so shrink is unconditionally true here. (The
              Status/Role Selects alongside this one don't need the same
              fix — their values default to 'all', never '', so MUI's
              default value-truthiness shrink logic already gets it right.) */}
          <InputLabel id="students-class-filter-label" shrink>Class</InputLabel>
          <Select
            labelId="students-class-filter-label"
            label="Class"
            displayEmpty
            value={qs.classId}
            onChange={(e) => setQueryState({ classId: e.target.value, page: QUERY_DEFAULTS.page })}
            sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
          >
            <MenuItem value="">
              <em>All classes</em>
            </MenuItem>
            {classOptions.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {gradeFilterOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            {/* Same `displayEmpty`/`shrink` pairing the Class filter above
                uses — this Select's default value is also "" ("All
                grades"), not a sentinel like Status/Access's 'all'. */}
            <InputLabel id="students-grade-filter-label" shrink>Grade</InputLabel>
            <Select
              labelId="students-grade-filter-label"
              label="Grade"
              displayEmpty
              value={qs.grade}
              onChange={(e) => setQueryState({ grade: e.target.value, page: QUERY_DEFAULTS.page })}
              sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
            >
              <MenuItem value="">
                <em>All grades</em>
              </MenuItem>
              {gradeFilterOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="students-status-filter-label">Status</InputLabel>
          <Select
            labelId="students-status-filter-label"
            label="Status"
            value={qs.status}
            onChange={(e) => setQueryState({ status: e.target.value, page: QUERY_DEFAULTS.page })}
            sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(24,24,27,0.1)', display: { xs: 'none', sm: 'block' } }} />

        <Button
          size="small"
          onClick={() =>
            setQueryState({
              sort: 'name',
              order: qs.sort === 'name' && qs.order === 'asc' ? 'desc' : 'asc',
              page: QUERY_DEFAULTS.page,
            })
          }
          startIcon={
            qs.sort === 'name' && qs.order === 'desc' ? (
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            ) : (
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            ...TOUCH_HIT_SX,
            fontWeight: 600,
            color: qs.sort === 'name' ? adminColors.accent : adminColors.inkSoft,
            '&:hover': { backgroundColor: adminColors.surface },
          }}
        >
          Name {qs.sort === 'name' && qs.order === 'desc' ? 'Z–A' : 'A–Z'}
        </Button>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        sortKey={qs.sort}
        sortDirection={qs.order}
        onSortChange={(key, dir) => setQueryState({ sort: key, order: dir, page: QUERY_DEFAULTS.page })}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkActions={bulkActions}
        onRowClick={handleRowClick}
        getRowAriaLabel={(row) => `Open ${row.username}`}
        loading={loadingList}
        emptyState={emptyStateNode}
      />

      {total > 0 && (
        // Dimmed (not hidden — hiding it would jump the layout) while a
        // refetch is in flight, so this footer's range/total never reads as
        // current for rows DataTable is simultaneously showing as loading
        // skeletons.
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            mt: 3,
            opacity: loadingList ? 0.5 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={qs.page}
              onChange={(_, v) => setQueryState({ page: v })}
              color="primary"
              shape="rounded"
            />
          )}
          <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
            {rangeStart}–{rangeEnd} of {total}
          </Typography>
        </Box>
      )}

      {/* Bulk: Add to class */}
      <Dialog open={bulkDialog === 'add'} onClose={closeBulkDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Add {selectedIds.length} student{selectedIds.length === 1 ? '' : 's'} to a class
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="bulk-add-class-label">Class</InputLabel>
            <Select
              labelId="bulk-add-class-label"
              label="Class"
              value={addClassTarget}
              onChange={(e) => setAddClassTarget(e.target.value)}
              disabled={addClassBusy}
            >
              {sortedGroupsForPicker.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                  {g.school ? ` · ${g.school}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeBulkDialog} disabled={addClassBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkAdd}
            disabled={!addClassTarget || addClassBusy}
            sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
          >
            {addClassBusy && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk: Remove from class */}
      <Dialog open={bulkDialog === 'remove'} onClose={closeBulkDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Remove {selectedIds.length} student{selectedIds.length === 1 ? '' : 's'} from a class
        </DialogTitle>
        <DialogContent>
          {removableClassOptions.length === 0 ? (
            <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
              None of the selected students are in any class.
            </Typography>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="bulk-remove-class-label">Class</InputLabel>
              <Select
                labelId="bulk-remove-class-label"
                label="Class"
                value={removeClassTarget}
                onChange={(e) => setRemoveClassTarget(e.target.value)}
                disabled={removeClassBusy}
              >
                {removableClassOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                    {g.school ? ` · ${g.school}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeBulkDialog} disabled={removeClassBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkRemove}
            disabled={!removeClassTarget || removeClassBusy}
          >
            {removeClassBusy && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk: Move school */}
      <Dialog open={bulkDialog === 'moveSchool'} onClose={closeBulkDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Move {selectedIds.length} student{selectedIds.length === 1 ? '' : 's'} to a new school
        </DialogTitle>
        <DialogContent>
          {!moveSchoolResult ? (
            <>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                This only changes the School field — class enrollments aren't touched. A real transfer check
                (flagging schedule conflicts before the move) is coming in a later phase.
              </Alert>
              <SchoolPicker
                value={moveSchoolTarget}
                onChange={setMoveSchoolTarget}
                currentUserRole={currentUserRole}
                allowUnassigned={false}
                label="Target school"
                required
              />
              {moveSchoolBusy && moveSchoolProgress && (
                <Box sx={{ mt: 2.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(moveSchoolProgress.done / Math.max(1, moveSchoolProgress.total)) * 100}
                  />
                  <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, mt: 0.5 }}>
                    Moving {moveSchoolProgress.done} of {moveSchoolProgress.total}…
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: adminColors.ink }}>
                {moveSchoolResult.succeeded.length} moved
                {moveSchoolResult.failed.length ? `, ${moveSchoolResult.failed.length} failed` : ''}.
              </Typography>
              {moveSchoolResult.failed.length > 0 && (
                <Box sx={{ maxHeight: 160, overflowY: 'auto', border: `1px solid ${theme.palette.grey[200]}`, borderRadius: radius.control, p: 1.5 }}>
                  {moveSchoolResult.failed.map((f) => (
                    <Typography key={f.id} variant="caption" sx={{ display: 'block', color: '#dc2626' }}>
                      {f.username}: {f.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {!moveSchoolResult ? (
            <>
              <Button onClick={closeBulkDialog} disabled={moveSchoolBusy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleMoveSchool}
                disabled={!moveSchoolTarget || moveSchoolBusy}
                sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
              >
                {moveSchoolBusy && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
                Move
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={finishMoveSchool}
              sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <StudentDrawer
        username={username}
        onClose={closeDrawer}
        groups={groups}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        notify={notify}
        confirmAction={confirmAction}
        onRoleChange={onRoleChange}
        onSchoolChange={onSchoolChange}
        onAccessToggle={onAccessToggle}
        onDeleteUser={onDeleteUser}
        onResetPassword={onResetPassword}
        onMutated={fetchList}
      />
    </Box>
  );
}
